/**
 * AdminUsageBillingPage — AI usage metering & billing.
 * Super-admin: edit the rate card, set per-plan/per-tenant margins, and explore
 * usage across all tenants (provider cost vs billed USD, with effective margin).
 * Tenant admins: see their own usage and billed totals.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { DollarSign, Loader2, Save, Trash2, RefreshCw, TrendingUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const inputCls = 'px-2 py-1 rounded-lg bg-muted/40 border border-border/50 text-sm text-foreground outline-none focus:border-primary/50';
const usd = (n) => `$${Number(n || 0).toFixed(6).replace(/0+$/, '').replace(/\.$/, '.0')}`;
const usd2 = (n) => `$${Number(n || 0).toFixed(4)}`;

function SuperAdminView() {
  const [pricing, setPricing] = useState([]);
  const [margins, setMargins] = useState([]);
  const [usage, setUsage] = useState(null);
  const [groupBy, setGroupBy] = useState('tenant_id');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [newRow, setNewRow] = useState({ modality: 'chat', provider: 'openai', model: '', input_rate_per_1k: 0, output_rate_per_1k: 0 });
  const [recon, setRecon] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m, u, r] = await Promise.all([api.getAiPricing(), api.getAiMargins(), api.getAiUsage(days, groupBy), api.getAiReconciliation(days)]);
      setPricing(p.items || []); setMargins(m.items || []); setUsage(u); setRecon(r);
    } catch (e) { toast.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [days, groupBy]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const savePricing = async (row) => {
    const key = `${row.modality}/${row.provider}/${row.model}`;
    setSavingKey(key);
    try { await api.upsertAiPricing(row); toast.success('Rate saved'); }
    catch (e) { toast.error(e.message); }
    finally { setSavingKey(null); }
  };

  const addPricing = async () => {
    if (!newRow.model.trim()) { toast.error('Enter a model name'); return; }
    try { await api.upsertAiPricing(newRow); toast.success('Model added'); setNewRow({ ...newRow, model: '' }); loadAll(); }
    catch (e) { toast.error(e.message); }
  };

  const delPricing = async (row) => {
    if (!window.confirm(`Delete rate for ${row.model}?`)) return;
    try { await api.deleteAiPricing(row.modality, row.provider, row.model); loadAll(); }
    catch (e) { toast.error(e.message); }
  };

  const saveMargin = async (cfg) => {
    setSavingKey(cfg.scope);
    try { await api.upsertAiMargin(cfg); toast.success('Margin saved'); }
    catch (e) { toast.error(e.message); }
    finally { setSavingKey(null); }
  };

  const upd = (list, setList, idx, field, val) => {
    const c = [...list]; c[idx] = { ...c[idx], [field]: val }; setList(c);
  };

  return (
    <div className="space-y-8">
      {/* Usage explorer */}
      <section className="rounded-2xl border border-border/50 bg-card/40 p-5" data-testid="usage-explorer">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground"><TrendingUp className="w-4 h-4 text-primary" /> Usage across all tenants</div>
          <div className="flex items-center gap-2">
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className={inputCls} data-testid="usage-groupby">
              {['tenant_id', 'model', 'provider', 'action_type', 'user_id', 'modality'].map(g => <option key={g} value={g}>{g.replace('_', ' ')}</option>)}
            </select>
            <select value={days} onChange={e => setDays(Number(e.target.value))} className={inputCls} data-testid="usage-days">
              <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
            </select>
            <Button size="sm" variant="outline" onClick={loadAll}><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        {usage && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[['Provider cost', usd2(usage.totals.provider_cost_usd)], ['Billed', usd2(usage.totals.billed_usd)],
              ['Margin', usage.totals.provider_cost_usd ? `${(usage.totals.billed_usd / usage.totals.provider_cost_usd).toFixed(2)}×` : '—'],
              ['Calls', usage.totals.calls]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-muted/30 border border-border/40 p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
                <p className="text-lg font-semibold text-foreground">{v}</p>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-muted-foreground border-b border-border/40">
              <th className="py-2 pr-4">{groupBy.replace('_', ' ')}</th><th className="py-2 pr-4">Calls</th><th className="py-2 pr-4">Tokens</th><th className="py-2 pr-4">Provider cost</th><th className="py-2 pr-4">Billed</th><th className="py-2">Margin</th>
            </tr></thead>
            <tbody>
              {(usage?.items || []).map((r) => (
                <tr key={r.key} className="border-b border-border/20" data-testid={`usage-row-${r.key}`}>
                  <td className="py-1.5 pr-4 font-medium text-foreground">{r.key}</td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{r.calls}</td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{r.tokens}</td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{usd2(r.provider_cost_usd)}</td>
                  <td className="py-1.5 pr-4 text-foreground">{usd2(r.billed_usd)}</td>
                  <td className="py-1.5">{r.margin_effective ? `${r.margin_effective}×` : '—'}</td>
                </tr>
              ))}
              {!loading && !(usage?.items || []).length && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No usage yet in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Margins */}
      <section className="rounded-2xl border border-border/50 bg-card/40 p-5" data-testid="margins-card">
        <div className="text-sm font-medium text-foreground mb-3">Margins (billed = provider cost × margin)</div>
        <div className="space-y-2">
          {margins.map((m, i) => (
            <div key={m.scope} className="flex items-center gap-3 flex-wrap" data-testid={`margin-${m.scope}`}>
              <span className="text-sm text-foreground w-40 truncate">{m.scope}</span>
              <label className="text-xs text-muted-foreground">×</label>
              <Input value={m.margin_multiplier} onChange={e => upd(margins, setMargins, i, 'margin_multiplier', e.target.value)} className={`${inputCls} w-20`} data-testid={`margin-input-${m.scope}`} />
              <label className="text-xs text-muted-foreground">Monthly cap $</label>
              <Input value={m.monthly_usd_cap || 0} onChange={e => upd(margins, setMargins, i, 'monthly_usd_cap', e.target.value)} className={`${inputCls} w-24`} />
              <Button size="sm" variant="outline" disabled={savingKey === m.scope} onClick={() => saveMargin(m)} data-testid={`margin-save-${m.scope}`}>
                {savingKey === m.scope ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Rate card */}
      <section className="rounded-2xl border border-border/50 bg-card/40 p-5" data-testid="ratecard">
        <div className="text-sm font-medium text-foreground mb-3">Rate card (per 1K tokens · per image · per minute/1K chars)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-muted-foreground border-b border-border/40">
              <th className="py-2 pr-3">Modality</th><th className="py-2 pr-3">Provider</th><th className="py-2 pr-3">Model</th><th className="py-2 pr-3">Input</th><th className="py-2 pr-3">Output</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {pricing.map((r, i) => (
                <tr key={`${r.modality}/${r.provider}/${r.model}`} className="border-b border-border/20">
                  <td className="py-1.5 pr-3"><Badge className="text-[10px]">{r.modality}</Badge></td>
                  <td className="py-1.5 pr-3 text-muted-foreground">{r.provider}</td>
                  <td className="py-1.5 pr-3 font-medium text-foreground">{r.model}</td>
                  <td className="py-1.5 pr-3"><Input value={r.input_rate_per_1k} onChange={e => upd(pricing, setPricing, i, 'input_rate_per_1k', e.target.value)} className={`${inputCls} w-24`} /></td>
                  <td className="py-1.5 pr-3"><Input value={r.output_rate_per_1k} onChange={e => upd(pricing, setPricing, i, 'output_rate_per_1k', e.target.value)} className={`${inputCls} w-24`} /></td>
                  <td className="py-1.5 flex gap-1">
                    <Button size="sm" variant="outline" disabled={savingKey === `${r.modality}/${r.provider}/${r.model}`} onClick={() => savePricing(r)} data-testid={`price-save-${r.model}`}>
                      {savingKey === `${r.modality}/${r.provider}/${r.model}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => delPricing(r)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 mt-4 flex-wrap" data-testid="add-price-row">
          <select value={newRow.modality} onChange={e => setNewRow({ ...newRow, modality: e.target.value })} className={inputCls}>
            {['chat', 'embedding', 'image', 'audio_stt', 'audio_tts'].map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <Input value={newRow.provider} onChange={e => setNewRow({ ...newRow, provider: e.target.value })} placeholder="provider" className={`${inputCls} w-28`} />
          <Input value={newRow.model} onChange={e => setNewRow({ ...newRow, model: e.target.value })} placeholder="model" className={`${inputCls} w-44`} data-testid="new-price-model" />
          <Input value={newRow.input_rate_per_1k} onChange={e => setNewRow({ ...newRow, input_rate_per_1k: e.target.value })} placeholder="input" className={`${inputCls} w-24`} />
          <Input value={newRow.output_rate_per_1k} onChange={e => setNewRow({ ...newRow, output_rate_per_1k: e.target.value })} placeholder="output" className={`${inputCls} w-24`} />
          <Button size="sm" onClick={addPricing} data-testid="add-price-btn">Add model</Button>
        </div>
      </section>

      {/* Rate-card vs proxy reconciliation */}
      <section className="rounded-2xl border border-border/50 bg-card/40 p-5" data-testid="reconciliation-card">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-1"><Sparkles className="w-4 h-4 text-primary" /> Cost reconciliation — rate card vs Emergent reported</div>
        <p className="text-xs text-muted-foreground mb-3">Positive drift = our rate-card estimate is higher than the proxy's actual cost. Big drift means a rate needs updating.</p>
        {recon && (
          <div className="mb-3 text-sm">
            <span className="text-muted-foreground">Totals: </span>
            <span className="text-foreground">rate card {usd2(recon.totals.ratecard_usd)} · reported {usd2(recon.totals.reported_usd)} · </span>
            <span className={Math.abs(recon.totals.drift_pct || 0) > 15 ? 'text-amber-400 font-medium' : 'text-foreground'}>drift {recon.totals.drift_pct ?? 0}%</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-muted-foreground border-b border-border/40">
              <th className="py-2 pr-4">Model</th><th className="py-2 pr-4">Calls</th><th className="py-2 pr-4">Rate card</th><th className="py-2 pr-4">Reported</th><th className="py-2 pr-4">Drift</th><th className="py-2">Drift %</th>
            </tr></thead>
            <tbody>
              {(recon?.items || []).map((r) => (
                <tr key={r.model} className="border-b border-border/20" data-testid={`recon-row-${r.model}`}>
                  <td className="py-1.5 pr-4 font-medium text-foreground">{r.model}</td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{r.calls}</td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{usd2(r.ratecard_usd)}</td>
                  <td className="py-1.5 pr-4 text-muted-foreground">{usd2(r.reported_usd)}</td>
                  <td className="py-1.5 pr-4 text-foreground">{usd2(r.drift_usd)}</td>
                  <td className={`py-1.5 ${Math.abs(r.drift_pct || 0) > 15 ? 'text-amber-400 font-medium' : 'text-muted-foreground'}`}>{r.drift_pct ?? '—'}%</td>
                </tr>
              ))}
              {!loading && !(recon?.items || []).length && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No proxy-reported costs to reconcile yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function TenantView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.getMyAiUsage(30).then(setData).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const cap = data?.cap;
  return (
    <div className="space-y-6" data-testid="tenant-usage">
      {cap?.cap > 0 && (cap.near || cap.over) && (
        <div className={`rounded-xl border p-3 text-sm ${cap.over ? 'border-destructive/40 bg-destructive/10 text-destructive' : 'border-amber-500/40 bg-amber-500/10 text-amber-500'}`} data-testid="cap-banner">
          {cap.over
            ? `Monthly AI budget reached — $${cap.spent} of $${cap.cap}. New AI requests are paused until the cap is raised or the month resets.`
            : `Heads up: you've used ${cap.pct}% of your $${cap.cap} monthly AI budget ($${cap.spent}).`}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-muted/30 border border-border/40 p-4">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">This month</p>
          <p className="text-2xl font-semibold text-foreground">{usd2(data?.this_month?.billed_usd)}</p>
          <p className="text-xs text-muted-foreground">{data?.this_month?.calls || 0} AI calls</p>
        </div>
        <div className="rounded-xl bg-muted/30 border border-border/40 p-4">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Last 30 days</p>
          <p className="text-2xl font-semibold text-foreground">{usd2(data?.period_billed_usd)}</p>
        </div>
      </div>
      <div className="rounded-2xl border border-border/50 bg-card/40 p-5">
        <div className="text-sm font-medium text-foreground mb-3">Billed by feature (30 days)</div>
        <table className="w-full text-sm">
          <tbody>
            {(data?.by_action || []).map(r => (
              <tr key={r.action_type} className="border-b border-border/20">
                <td className="py-1.5 font-medium text-foreground capitalize">{(r.action_type || 'adhoc').replace(/_/g, ' ')}</td>
                <td className="py-1.5 text-right text-muted-foreground">{r.calls} calls</td>
                <td className="py-1.5 text-right text-foreground w-24">{usd2(r.billed_usd)}</td>
              </tr>
            ))}
            {!(data?.by_action || []).length && <tr><td className="py-6 text-center text-muted-foreground">No AI usage yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminUsageBillingPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="usage-billing-page">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary"><DollarSign className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">AI Usage &amp; Billing</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isSuperAdmin
              ? 'Rate card, per-plan and per-tenant margins, and metered usage across all tenants — provider cost vs billed USD.'
              : 'Your organization’s AI usage and billed amounts, by feature.'}
          </p>
        </div>
      </div>
      {isSuperAdmin ? <SuperAdminView /> : <TenantView />}
    </div>
  );
}
